import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getConfig, updateConfig, testMetaConnection, testCapiConnection, testDataCrazyConnection, getAdAccounts, toggleAdAccount, deleteAdAccount, selectAllAdAccounts, listBusinessManagers, addBusinessManager, discoverBMAccounts, discoverAllBMAccounts, toggleBusinessManager, deleteBusinessManager, renameBM, listRoles, createRole, updateRole, deleteRole, assignRole, getPlans, getMySubscription, getPlanUsage } from '../services/api';
import { Save, Pencil, Loader2, CheckCircle, XCircle, X, Eye, EyeOff, RefreshCw, Building2, CreditCard, Trash2, Plus, ChevronDown, ChevronRight, Key, Globe, ToggleLeft, ToggleRight, Settings as SettingsIcon, User, Users, Bell, Send, Link2, Unlink, Smartphone, BellOff, MessageSquare, TrendingUp, AlertTriangle, Check, Shield, Lock, CreditCard as CreditCardIcon, Star, Crown, Zap as ZapIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isPushSupported, isSubscribed, subscribeToPush, unsubscribeFromPush, sendTestNotification } from '../services/pushService';
import api from '../services/api';

const Settings = () => {
  const { user, isAdmin } = useAuth();
  const [searchParams] = typeof useSearchParams === 'function' ? useSearchParams() : [new URLSearchParams()];
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams?.get?.('tab') || new URLSearchParams(window.location.search).get('tab');
    return tabParam || 'account';
  });


  const [roles, setRoles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [planUsage, setPlanUsage] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', permissions: {} });
  const MODULES = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'sales', name: 'Vendas' },
    { id: 'campaigns', name: 'Campanhas' },
    { id: 'automations', name: 'Automações' },
    { id: 'sellers', name: 'Gestão Comercial' },
    { id: 'webhooks', name: 'Webhooks' },
    { id: 'whatsapp', name: 'Saúde WhatsApp' },
    { id: 'settings', name: 'Configurações' },
  ];
  const ACTIONS = ['view', 'edit', 'delete'];
  const ACTION_LABELS = { view: 'Visualizar', edit: 'Editar', delete: 'Excluir' };

  const tabs = [
    { id: 'account', label: 'Minha Conta', icon: User },
    { id: 'notifications', label: 'Notificações', icon: Bell },
  ];
  if (isAdmin && isAdmin()) {
    tabs.push({ id: 'users', label: 'Usuários', icon: Users });
    tabs.push({ id: 'roles', label: 'Cargos', icon: Shield });
    tabs.push({ id: 'plan', label: 'Minha Assinatura', icon: Crown });
  }

  const loadPlans = async () => {
    try {
      const [pRes, sRes, uRes] = await Promise.all([getPlans(), getMySubscription(), getPlanUsage()]);
      setPlans(pRes.data.plans || []);
      setSubscription(sRes.data.subscription || null);
      setPlanUsage(uRes.data.usage || null);
    } catch(e) {}
  };
  useEffect(() => { loadPlans(); }, []);

  const loadRoles = async () => { try { const r = await listRoles(); setRoles(r.data.roles || []); } catch(e){} };
  useEffect(() => { if (isAdmin && isAdmin()) loadRoles(); }, []);
  const handleSaveRole = async () => {
    try {
      if (editingRole) { await updateRole(editingRole.id, roleForm); }
      else { await createRole(roleForm); }
      setShowRoleModal(false); setEditingRole(null); setRoleForm({ name: '', permissions: {} }); loadRoles();
    } catch(e) { console.error(e); }
  };
  const handleDeleteRole = async (id) => { if (!window.confirm('Excluir este cargo?')) return; try { await deleteRole(id); loadRoles(); } catch(e){} };
  const togglePerm = (mod, action) => {
    setRoleForm(prev => {
      const p = { ...prev.permissions };
      if (!p[mod]) p[mod] = [];
      if (p[mod].includes(action)) p[mod] = p[mod].filter(a => a !== action);
      else p[mod] = [...p[mod], action];
      return { ...prev, permissions: p };
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Configurações</h1>
        <p className="text-gray-400">Gerencie suas integrações, conta e preferências</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-800 p-1 rounded-xl overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700'}`}>
            <tab.icon size={18} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === 'integrations' && (
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <p className="text-gray-400 mb-3">As integrações agora ficam no menu <span className="text-white font-semibold">Integrações</span>.</p>
          <button onClick={() => window.location.href = '/webhooks?tab=meta'} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Ir para Integrações</button>
        </div>
      )}
      {activeTab === 'account' && <AccountTab />}
      {activeTab === 'notifications' && <NotificationsTab />}
      {activeTab === 'users' && <UsersTab />}

      {/* ===== PLANO TAB ===== */}
      {activeTab === 'plan' && (
        <div className="space-y-6">
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Crown size={20} className="text-yellow-400" />Minha Assinatura</h2>

          {subscription && (
            <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-6 border border-blue-700/50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Plano atual</p>
                  <h3 className="text-2xl font-bold text-white mt-1">{subscription.planName}</h3>
                  <p className="text-gray-400 text-sm mt-1">Status: <span className={subscription.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>{subscription.status === 'active' ? 'Ativo' : subscription.status === 'trial' ? 'Trial' : subscription.status}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">R$ {parseFloat(subscription.price || 0).toFixed(2)}</p>
                  <p className="text-gray-500 text-sm">/mês</p>
                </div>
              </div>
            </div>
          )}

          {planUsage && subscription?.limits_config && (
            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <h3 className="text-white font-bold mb-4">Uso do Plano</h3>
              <div className="space-y-3">
                {[
                  { label: 'Business Managers', used: planUsage.bms, limit: subscription.limits_config.bms },
                  { label: 'Contas de Anúncio', used: planUsage.adAccounts, limit: subscription.limits_config.adAccounts },
                  { label: 'Vendedores', used: planUsage.sellers, limit: subscription.limits_config.sellers },
                  { label: 'Automações', used: planUsage.automations, limit: subscription.limits_config.automations },
                  { label: 'Usuários', used: planUsage.users, limit: subscription.limits_config.users },
                ].map(item => {
                  const pct = Math.min((item.used / (item.limit || 1)) * 100, 100);
                  return (
                    <div key={item.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">{item.label}</span>
                        <span className="text-white">{item.used} / {item.limit >= 999 ? 'Ilimitado' : item.limit}</span>
                      </div>
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-blue-500'}`} style={{ width: pct + '%' }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((plan, idx) => {
              const isCurrent = subscription?.planId === plan.id;
              const colors = ['border-gray-600', 'border-blue-500 ring-2 ring-blue-500/20', 'border-purple-500'];
              const icons = [<Star size={24} className="text-gray-400" />, <ZapIcon size={24} className="text-blue-400" />, <Crown size={24} className="text-purple-400" />];
              return (
                <div key={plan.id} className={`bg-gray-800 rounded-xl p-6 border ${isCurrent ? 'border-green-500 ring-2 ring-green-500/20' : colors[idx] || 'border-gray-700'}`}>
                  <div className="flex items-center gap-3 mb-4">{icons[idx]}<h3 className="text-xl font-bold text-white">{plan.name}</h3></div>
                  <p className="text-3xl font-bold text-white mb-1">R$ {parseFloat(plan.price || 0).toFixed(2)}<span className="text-gray-500 text-base font-normal">/mês</span></p>
                  <ul className="space-y-2 mt-4">
                    {(plan.features || []).map((f, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-300 text-sm"><Check size={16} className="text-green-400 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <button className={`w-full mt-6 py-2.5 rounded-lg font-medium ${isCurrent ? 'bg-green-600/20 text-green-400 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-500'}`} disabled={isCurrent}>
                    {isCurrent ? 'Plano Atual' : plan.price === 0 ? 'Selecionar' : 'Upgrade'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

{/* ===== CARGOS TAB ===== */}
      {activeTab === 'roles' && isAdmin() && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Shield size={20} className="text-purple-400" />Cargos e Permissões</h2>
            <button onClick={() => { setEditingRole(null); setRoleForm({ name: '', permissions: {} }); setShowRoleModal(true); }} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-500"><Plus size={18} />Novo Cargo</button>
          </div>

          {roles.length === 0 ? (
            <div className="bg-gray-800 rounded-xl p-12 text-center"><Shield size={48} className="mx-auto text-gray-600 mb-4" /><p className="text-gray-400">Nenhum cargo criado</p><p className="text-gray-500 text-sm mt-1">Crie cargos para controlar o que cada usuário pode acessar</p></div>
          ) : (
            <div className="space-y-4">
              {roles.map(role => (
                <div key={role.id} className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2"><Lock size={16} className="text-purple-400" />{role.name}</h3>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingRole(role); setRoleForm({ name: role.name, permissions: role.permissions || {} }); setShowRoleModal(true); }} className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600">Editar</button>
                      <button onClick={() => handleDeleteRole(role.id)} className="px-3 py-1.5 bg-red-600/20 text-red-400 rounded-lg text-sm hover:bg-red-600/40">Excluir</button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {MODULES.map(mod => {
                      const perms = role.permissions?.[mod.id] || [];
                      if (perms.length === 0) return null;
                      return (
                        <div key={mod.id} className="px-3 py-1.5 bg-gray-700/50 rounded-lg">
                          <span className="text-gray-300 text-sm font-medium">{mod.name}: </span>
                          <span className="text-purple-400 text-sm">{perms.map(p => ACTION_LABELS[p]).join(', ')}</span>
                        </div>
                      );
                    })}
                    {Object.keys(role.permissions || {}).every(k => (role.permissions[k] || []).length === 0) && <span className="text-gray-500 text-sm">Sem permissoes configuradas</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {showRoleModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white">{editingRole ? 'Editar Cargo' : 'Novo Cargo'}</h3>
                  <button onClick={() => setShowRoleModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                  <div><label className="text-gray-400 text-sm mb-1 block">Nome do cargo</label><input type="text" value={roleForm.name} onChange={(e) => setRoleForm(p => ({ ...p, name: e.target.value }))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white" placeholder="Ex: Gestor de Trafego" /></div>
                  <div>
                    <label className="text-gray-400 text-sm mb-3 block">Permissoes por módulo</label>
                    <div className="space-y-2">
                      {MODULES.map(mod => (
                        <div key={mod.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                          <span className="text-white text-sm font-medium">{mod.name}</span>
                          <div className="flex gap-2">
                            {ACTIONS.map(act => (
                              <button key={act} onClick={() => togglePerm(mod.id, act)} className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${(roleForm.permissions?.[mod.id] || []).includes(act) ? 'bg-purple-600 text-white' : 'bg-gray-600 text-gray-400 hover:text-white'}`}>{ACTION_LABELS[act]}</button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="p-6 border-t border-gray-700 flex gap-3">
                  <button onClick={() => setShowRoleModal(false)} className="flex-1 py-2.5 bg-gray-700 text-white rounded-lg">Cancelar</button>
                  <button onClick={handleSaveRole} disabled={!roleForm.name} className="flex-1 py-2.5 bg-purple-600 text-white rounded-lg disabled:opacity-50">{editingRole ? 'Salvar' : 'Criar'}</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
};

// ==================== INTEGRATIONS TAB ====================
export const IntegrationsTab = () => {
  const [config, setConfig] = useState({ metaPixelId: '', metaAccessToken: '', metaAdAccountId: '', datacrazyToken: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTokens, setShowTokens] = useState({});
  const [testResults, setTestResults] = useState({});
  const [message, setMessage] = useState(null);
  const [bms, setBms] = useState([]);
  const [showAddBM, setShowAddBM] = useState(false);
  const [newBMToken, setNewBMToken] = useState('');
  const [pixels, setPixels] = useState([]);
  const [capiEnabled, setCapiEnabled] = useState(false);
  const [capiLogs, setCapiLogs] = useState([]);
  const [capiStats, setCapiStats] = useState(null);
  const [showCapiLogs, setShowCapiLogs] = useState(false);
  const [capiLoading, setCapiLoading] = useState(false);
  const [selectedCapiLog, setSelectedCapiLog] = useState(null);
  const [showAddPixel, setShowAddPixel] = useState(false);
  const AVAILABLE_META_EVENTS = [
    { id: 'Purchase', label: 'Purchase', desc: 'Venda aprovada' },
    { id: 'InitiateCheckout', label: 'InitiateCheckout', desc: 'Início de checkout' },
    { id: 'AddPaymentInfo', label: 'AddPaymentInfo', desc: 'Pix gerado / pagto iniciado' },
    { id: 'Lead', label: 'Lead', desc: 'Lead capturado' },
    { id: 'AddToCart', label: 'AddToCart', desc: 'Adicionou ao carrinho' },
    { id: 'ViewContent', label: 'ViewContent', desc: 'Visualizou produto' },
    { id: 'CompleteRegistration', label: 'CompleteRegistration', desc: 'Completou cadastro' },
    { id: 'Subscribe', label: 'Subscribe', desc: 'Assinatura ativa' },
  ];
  const [pixelForm, setPixelForm] = useState({ pixelId: '', pixelName: '', accessToken: '', productCodes: '', isDefault: false, enabledEvents: ['Purchase'] });
  const [editingPixel, setEditingPixel] = useState(null);
  const [newBMName, setNewBMName] = useState('');
  const [addingBM, setAddingBM] = useState(false);
  const [editingBMName, setEditingBMName] = useState(null);
  const [fbConnecting, setFbConnecting] = useState(false);
  const [fbDiscovered, setFbDiscovered] = useState(null);

  // Processar Facebook OAuth code da URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      window.history.replaceState({}, '', '/settings');
      (async () => {
        try {
          setFbConnecting(true);
          const redirectUri = window.location.origin + '/settings';
          const res = await api.post('/facebook-auth/exchange-code', { code, redirectUri });
          const token = res.data.accessToken;
          const discRes = await api.post('/facebook-auth/discover-all', { accessToken: token });
          setFbDiscovered({ businesses: discRes.data.businesses, token });
        } catch (e) {
          console.error('FB Error:', e);
          alert('Erro ao conectar: ' + (e.response?.data?.error || e.message));
        }
        setFbConnecting(false);
      })();
    }
  }, []);
  const [fbSettingUp, setFbSettingUp] = useState({});
  const [bmNewName, setBmNewName] = useState('');
  const [discoveringBM, setDiscoveringBM] = useState({});
  const [discoveringAll, setDiscoveringAll] = useState(false);
  const [adAccounts, setAdAccounts] = useState([]);
  const [groupedAccounts, setGroupedAccounts] = useState([]);
  const [expandedBMs, setExpandedBMs] = useState({});

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getConfig();
      setConfig({ metaPixelId: response.data.config.metaPixelId || '', metaAccessToken: '', metaAdAccountId: response.data.config.metaAdAccountId || '', datacrazyToken: '' });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchBMs = async () => {
    try {
      const response = await listBusinessManagers();
      setBms(response.data.businessManagers || []);
    } catch (e) { console.error(e); }
  };

  const fetchAdAccounts = async () => {
    try {
      const response = await getAdAccounts();
      setAdAccounts(response.data.accounts || []);
      setGroupedAccounts(response.data.grouped || []);
      const expanded = {};
      (response.data.grouped || []).forEach(g => { expanded[g.businessManagerId || 'no_bm'] = true; });
      setExpandedBMs(expanded);
    } catch (e) { console.error(e); }
  };

  const fetchPixels = async () => {
    try {
      const r = await api.get('/pixels');
      setPixels(r.data.pixels || []);
    } catch(e) { console.error(e); }
  };
  const handleAddPixel = async () => {
    if (!pixelForm.pixelId) return;
    const payload = {
      pixelId: pixelForm.pixelId,
      pixelName: pixelForm.pixelName,
      accessToken: pixelForm.accessToken,
      productCodes: pixelForm.productCodes
        ? pixelForm.productCodes.split(',').map(s => s.trim()).filter(Boolean)
        : [],
      isDefault: !!pixelForm.isDefault,
      enabledEvents: Array.isArray(pixelForm.enabledEvents) && pixelForm.enabledEvents.length ? pixelForm.enabledEvents : ['Purchase']
    };
    try {
      if (editingPixel) {
        await api.put('/pixels/' + editingPixel.id, { ...payload, isActive: true });
      } else {
        await api.post('/pixels', payload);
      }
      setPixelForm({ pixelId: '', pixelName: '', accessToken: '', productCodes: '', isDefault: false, enabledEvents: ['Purchase'] });
      setEditingPixel(null);
      setShowAddPixel(false);
      fetchPixels();
    } catch(e) { console.error(e); alert('Erro ao salvar pixel'); }
  };
  const handleEditPixel = (px) => {
    setEditingPixel(px);
    setPixelForm({
      pixelId: px.pixelId || '',
      pixelName: px.pixelName || '',
      accessToken: '',
      productCodes: Array.isArray(px.productCodes) ? px.productCodes.join(', ') : '',
      isDefault: !!px.isDefault,
      enabledEvents: Array.isArray(px.enabledEvents) && px.enabledEvents.length ? px.enabledEvents : ['Purchase']
    });
    setShowAddPixel(true);
  };
  const handleCancelPixel = () => {
    setEditingPixel(null);
    setPixelForm({ pixelId: '', pixelName: '', accessToken: '', productCodes: '', isDefault: false, enabledEvents: ['Purchase'] });
    setShowAddPixel(false);
  };
  const handleDeletePixel = async (id) => {
    if (!window.confirm('Remover este pixel?')) return;
    try {
      await api.delete('/pixels/' + id);
      fetchPixels();
    } catch(e) { console.error(e); }
  };

  const loadCapiStatus = async () => {
    try { const r = await api.get('/capi/status'); setCapiEnabled(r.data.enabled); } catch(e) {}
  };
  const toggleCapi = async () => {
    try {
      setCapiLoading(true);
      const r = await api.post('/capi/toggle', { enabled: !capiEnabled });
      setCapiEnabled(r.data.enabled);
    } catch(e) { console.error(e); } finally { setCapiLoading(false); }
  };
  const loadCapiLogs = async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        api.get('/capi-logs', { params: { limit: 10 } }),
        api.get('/capi-logs/stats')
      ]);
      setCapiLogs(logsRes.data.data || []);
      setCapiStats(statsRes.data.stats || []);
    } catch(e) {}
  };

  useEffect(() => { fetchConfig(); fetchBMs(); fetchAdAccounts(); fetchPixels(); loadCapiStatus(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const d = { ...config };
      if (!d.metaAccessToken) delete d.metaAccessToken;
      if (!d.datacrazyToken) delete d.datacrazyToken;
      await updateConfig(d);
      setMessage({ type: 'success', text: 'Configurações salvas!' });
      setConfig(prev => ({ ...prev, metaAccessToken: '', datacrazyToken: '' }));
    } catch (e) { setMessage({ type: 'error', text: 'Erro ao salvar' }); }
    setSaving(false);
  };

  const testConnection = async (type) => {
    setTestResults(prev => ({ ...prev, [type]: 'loading' }));
    try {
      let r;
      if (type === 'meta') r = await testMetaConnection();
      else if (type === 'capi') r = await testCapiConnection();
      else if (type === 'datacrazy') r = await testDataCrazyConnection();
      const ok = r.data.success;
      setTestResults(prev => ({ ...prev, [type]: ok ? 'success' : 'error' }));
      const labels = { meta: 'Marketing API', capi: 'CAPI', datacrazy: 'DataCrazy' };
      setMessage({ type: ok ? 'success' : 'error', text: ok ? `${labels[type]} conectado com sucesso!` : `Erro ao conectar ${labels[type]}` });
    } catch (e) { 
      setTestResults(prev => ({ ...prev, [type]: 'error' }));
      setMessage({ type: 'error', text: 'Erro ao testar conexão' });
    }
  };

  const handleFacebookLogin = () => {
    const appId = '1558761015351520';
    const redirectUri = encodeURIComponent(window.location.origin + '/settings');
    const scope = 'business_management,ads_management,ads_read';
    const authUrl = 'https://www.facebook.com/v18.0/dialog/oauth?client_id=' + appId + '&redirect_uri=' + redirectUri + '&scope=' + scope + '&response_type=code';
    window.location.href = authUrl;
  };

  // Processar code pendente do redirect


  const handleSetupBM = async (bm) => {
    setFbSettingUp(prev => ({ ...prev, [bm.bmId]: true }));
    try {
      await api.post('/facebook-auth/setup-bm', { accessToken: fbDiscovered.token, bmId: bm.bmId, bmName: bm.bmName });
      setFbSettingUp(prev => ({ ...prev, [bm.bmId]: 'done' }));
      fetchBMs();
    } catch (e) {
      setFbSettingUp(prev => ({ ...prev, [bm.bmId]: 'error' }));
      alert('Erro: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleSetupAll = async () => {
    if (!fbDiscovered) return;
    for (const bm of fbDiscovered.businesses) {
      await handleSetupBM(bm);
    }
  };

  const handleRenameBM = async (id) => {
    if (!bmNewName.trim()) return;
    try { await renameBM(id, bmNewName.trim()); setEditingBMName(null); setBmNewName(''); fetchBMs(); } catch(e) {}
  };

  const handleAddBM = async () => {
    if (!newBMToken.trim()) return;
    setAddingBM(true);
    try {
      const r = await addBusinessManager({ accessToken: newBMToken.trim(), tokenName: newBMName.trim() || undefined });
      setMessage({ type: 'success', text: r.data.message || 'BM adicionada!' });
      setNewBMToken(''); setNewBMName(''); setShowAddBM(false);
      await fetchBMs();
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erro' }); }
    setAddingBM(false);
  };

  const handleDiscoverBM = async (bmId) => {
    setDiscoveringBM(prev => ({ ...prev, [bmId]: true }));
    try {
      const r = await discoverBMAccounts(bmId);
      setMessage({ type: 'success', text: `${r.data.total} contas (${r.data.added} novas, ${r.data.updated} atualizadas)` });
      await fetchBMs(); await fetchAdAccounts();
    } catch (e) { setMessage({ type: 'error', text: 'Erro: ' + (e.response?.data?.error || e.message) }); }
    setDiscoveringBM(prev => ({ ...prev, [bmId]: false }));
  };

  const handleDiscoverAll = async () => {
    setDiscoveringAll(true);
    try {
      const r = await discoverAllBMAccounts();
      setMessage({ type: 'success', text: `${r.data.totalBMs} BMs: ${r.data.totalAccounts} contas (${r.data.added} novas)` });
      await fetchBMs(); await fetchAdAccounts();
    } catch (e) { setMessage({ type: 'error', text: 'Erro: ' + (e.response?.data?.error || e.message) }); }
    setDiscoveringAll(false);
  };

  const handleToggleBM = async (bmId) => {
    try { await toggleBusinessManager(bmId); setBms(prev => prev.map(b => b.id === bmId ? { ...b, isActive: !b.isActive } : b)); } catch (e) {}
  };

  const handleDeleteBM = async (bmId) => {
    if (!window.confirm('Remover esta BM e contas vinculadas?')) return;
    try { await deleteBusinessManager(bmId); await fetchBMs(); await fetchAdAccounts(); setMessage({ type: 'success', text: 'BM removida!' }); } catch (e) {}
  };

  const handleToggleAccount = async (id) => {
    try { await toggleAdAccount(id); setAdAccounts(prev => prev.map(a => a.id === id ? { ...a, isSelected: !a.isSelected } : a)); } catch (e) {}
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Remover esta conta?')) return;
    try { await deleteAdAccount(id); fetchAdAccounts(); } catch (e) {}
  };

  const handleSelectAll = async (selected) => {
    try { await selectAllAdAccounts(selected); setAdAccounts(prev => prev.map(a => ({ ...a, isSelected: selected }))); } catch (e) {}
  };

  const toggleBMExpand = (bmId) => setExpandedBMs(prev => ({ ...prev, [bmId]: !prev[bmId] }));
  const selectedCount = adAccounts.filter(a => a.isSelected).length;

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg flex items-center justify-between ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{message.text}</span>
          <button type="button" onClick={() => setMessage(null)} className="ml-2 opacity-60 hover:opacity-100">x</button>
        </div>
      )}

      {/* Meta Config */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Meta (Facebook)</h2>
        <div className="space-y-4">
          {/* Pixels Configurados */}
          <div className="bg-gray-900/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-medium text-sm">Pixels Configurados</h3>
              <button onClick={() => { if (showAddPixel) { handleCancelPixel(); } else { setShowAddPixel(true); } }} className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"><Plus size={14} />{showAddPixel ? 'Cancelar' : 'Adicionar Pixel'}</button>
            </div>
            {showAddPixel && (
              <div className="bg-gray-800 rounded-lg p-4 space-y-3 border border-blue-700/30">
                <h4 className="text-white text-sm font-medium">{editingPixel ? 'Editar Pixel' : 'Novo Pixel'}</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Nome</label>
                    <input type="text" value={pixelForm.pixelName} onChange={(e) => setPixelForm({...pixelForm, pixelName: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Ex: Gelatina" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Pixel ID</label>
                    <input type="text" value={pixelForm.pixelId} onChange={(e) => setPixelForm({...pixelForm, pixelId: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="9265009..." />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Access Token <span className="text-gray-600">(opcional - usa o token padrão da conta se vazio)</span></label>
                  <input type="text" value={pixelForm.accessToken || ''} onChange={(e) => setPixelForm({...pixelForm, accessToken: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="EAA... (deixe vazio para usar o token principal)" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Códigos de produto <span className="text-gray-600">(separados por vírgula)</span></label>
                  <input type="text" value={pixelForm.productCodes} onChange={(e) => setPixelForm({...pixelForm, productCodes: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono" placeholder="4ZJ28N, ABC123" />
                  <p className="text-gray-500 text-xs mt-1">Roteia vendas por product.code do webhook. Deixe vazio se não quiser filtrar por produto.</p>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-2">Eventos disparados pra Meta <span className="text-gray-600">(quais eventos esse pixel envia)</span></label>
                  <div className="grid grid-cols-2 gap-2">
                    {AVAILABLE_META_EVENTS.map(ev => {
                      const checked = (pixelForm.enabledEvents || []).includes(ev.id);
                      return (
                        <label key={ev.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-xs border ${checked ? 'bg-blue-900/30 border-blue-700/50' : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const current = pixelForm.enabledEvents || [];
                              const next = e.target.checked
                                ? [...current, ev.id]
                                : current.filter(x => x !== ev.id);
                              setPixelForm({...pixelForm, enabledEvents: next});
                            }}
                            className="w-4 h-4 accent-blue-500 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-white font-medium">{ev.label}</div>
                            <div className="text-gray-500 text-[10px]">{ev.desc}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {(pixelForm.enabledEvents || []).length === 0 && (
                    <p className="text-yellow-400 text-xs mt-2">⚠️ Sem eventos marcados — o pixel não receberá nada da Meta. Marque pelo menos Purchase.</p>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input type="checkbox" checked={!!pixelForm.isDefault} onChange={(e) => setPixelForm({...pixelForm, isDefault: e.target.checked})} className="w-4 h-4 accent-blue-500" />
                  <span className="text-gray-300">Usar este como pixel padrão (fallback quando product.code não casar com nenhum)</span>
                </label>
                <div className="flex gap-2">
                  <button onClick={handleCancelPixel} className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600">Cancelar</button>
                  <button onClick={handleAddPixel} disabled={!pixelForm.pixelId} className="flex-1 bg-blue-600 text-white rounded-lg px-3 py-2 text-sm hover:bg-blue-500 font-medium disabled:opacity-50">{editingPixel ? 'Salvar Alterações' : 'Adicionar Pixel'}</button>
                </div>
              </div>
            )}
            {/* Pixel Principal */}
            {config.metaPixelId && (
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-900/30 to-gray-800 border border-blue-800/50 rounded-lg p-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold">P</div>
                  <div>
                    <span className="text-white text-sm font-medium">Pixel Principal</span>
                    <span className="text-gray-400 text-xs ml-2 font-mono">{config.metaPixelId}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2"><span className="text-green-400 text-xs bg-green-400/10 px-2 py-1 rounded-full">Principal</span><button onClick={() => { if(window.confirm("Remover pixel principal?")) { setConfig({...config, metaPixelId: ""}); } }} className="text-red-400 hover:text-red-300 p-1"><Trash2 size={14} /></button></div>
              </div>
            )}
            {/* Pixels Adicionais */}
            {pixels.map(px => {
              const codes = Array.isArray(px.productCodes) ? px.productCodes : [];
              return (
                <div key={px.id} className={`flex items-center justify-between bg-gray-800 border rounded-lg p-3 ${px.isDefault ? 'border-yellow-600/50' : 'border-gray-700'}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${px.isDefault ? 'bg-yellow-600' : 'bg-gray-600'}`}>{(px.pixelName || 'P')[0].toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-medium truncate">{px.pixelName || 'Sem nome'}</span>
                        <span className="text-gray-400 text-xs font-mono">{px.pixelId}</span>
                        {px.isDefault && <span className="text-yellow-400 text-xs bg-yellow-400/10 px-2 py-0.5 rounded-full">Padrão</span>}
                      </div>
                      {codes.length > 0 ? (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="text-gray-500 text-xs">Códigos:</span>
                          {codes.map(c => (
                            <span key={c} className="text-blue-300 text-xs bg-blue-500/10 px-1.5 py-0.5 rounded font-mono">{c}</span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-xs mt-1">Sem códigos de produto — usado só como fallback se for Padrão</p>
                      )}
                      {Array.isArray(px.enabledEvents) && px.enabledEvents.length > 0 && (
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className="text-gray-500 text-xs">Eventos:</span>
                          {px.enabledEvents.map(ev => (
                            <span key={ev} className="text-green-300 text-xs bg-green-500/10 px-1.5 py-0.5 rounded">{ev}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className={`text-xs px-2 py-1 rounded-full ${px.isActive ? 'text-green-400 bg-green-400/10' : 'text-gray-500 bg-gray-700'}`}>{px.isActive ? 'Ativo' : 'Inativo'}</span>
                    <button onClick={() => handleEditPixel(px)} className="text-blue-400 hover:text-blue-300 p-1.5" title="Editar"><Pencil size={14} /></button>
                    <button onClick={() => handleDeletePixel(px.id)} className="text-red-400 hover:text-red-300 p-1.5" title="Excluir"><Trash2 size={14} /></button>
                  </div>
                </div>
              );
            })}
            {!config.metaPixelId && pixels.length === 0 && (
              <p className="text-gray-500 text-xs text-center py-2">Nenhum pixel configurado. Adicione seu primeiro pixel acima.</p>
            )}
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Access Token (em branco para manter)</label>
            <div className="relative">
              <input type={showTokens.meta ? 'text' : 'password'} value={config.metaAccessToken} onChange={(e) => setConfig({ ...config, metaAccessToken: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-blue-500" placeholder="Cole o novo token..." />
              <button type="button" onClick={() => setShowTokens({ ...showTokens, meta: !showTokens.meta })} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showTokens.meta ? <EyeOff size={20} /> : <Eye size={20} />}</button>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => testConnection('meta')} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2">
              {testResults.meta === 'loading' ? <Loader2 className="animate-spin" size={18} /> : testResults.meta === 'success' ? <CheckCircle className="text-green-400" size={18} /> : testResults.meta === 'error' ? <XCircle className="text-red-400" size={18} /> : null}Testar Marketing API
            </button>
            <button type="button" onClick={() => testConnection('capi')} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2">
              {testResults.capi === 'loading' ? <Loader2 className="animate-spin" size={18} /> : testResults.capi === 'success' ? <CheckCircle className="text-green-400" size={18} /> : testResults.capi === 'error' ? <XCircle className="text-red-400" size={18} /> : null}Testar CAPI
            </button>
          </div>
          {/* Meta CAPI - Conversions API */}
          <div className="mt-4 bg-gray-900/50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-medium text-sm flex items-center gap-2"><Send size={16} className="text-blue-400" />Meta Conversions API (CAPI)</h3>
                <p className="text-gray-500 text-xs mt-1">Envia eventos de conversão automaticamente para o Meta quando chegam vendas</p>
              </div>
              <button onClick={toggleCapi} disabled={capiLoading} className={"relative w-12 h-6 rounded-full transition-colors " + (capiEnabled ? "bg-green-600" : "bg-gray-600")}>
                <div className={"absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform " + (capiEnabled ? "translate-x-6" : "translate-x-0.5")}></div>
              </button>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className={"px-2 py-1 rounded-full " + (capiEnabled ? "bg-green-900/50 text-green-400" : "bg-gray-700 text-gray-400")}>{capiEnabled ? "Ativo" : "Desativado"}</span>
              <button onClick={() => { setShowCapiLogs(!showCapiLogs); if (!showCapiLogs) loadCapiLogs(); }} className="text-blue-400 hover:text-blue-300 flex items-center gap-1"><Eye size={14} />{showCapiLogs ? 'Ocultar Logs' : 'Ver Logs'}</button>
            </div>
            {showCapiLogs && (
              <div className="space-y-2">
                {capiStats && capiStats.length > 0 && (
                  <div className="flex gap-3">
                    {capiStats.map(s => (
                      <span key={s.status} className={"px-3 py-1 rounded-lg text-xs font-medium " + (s.status === 'success' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400')}>
                        {s.status === 'success' ? 'Enviados' : 'Erros'}: {s.count}
                      </span>
                    ))}
                  </div>
                )}
                {capiLogs.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {capiLogs.map((log, i) => (
                      <div key={i} onClick={() => setSelectedCapiLog(log)} className="flex items-center justify-between bg-gray-800 rounded-lg p-2 text-xs cursor-pointer hover:bg-gray-700/70 transition-colors">
                        <div className="flex items-center gap-2">
                          {log.status === 'success' ? <CheckCircle size={12} className="text-green-400" /> : <XCircle size={12} className="text-red-400" />}
                          <span className="text-white">{log.event_name || 'Purchase'}</span>
                          <span className="text-gray-400">{log.customer_name || log.customer_phone || '-'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">{log.value ? 'R$ ' + parseFloat(log.value).toFixed(2) : ''}</span>
                          <span className="text-gray-500">{log.created_at ? new Date(log.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs">Nenhum log nos últimos 30 dias</p>
                )}
              </div>
            )}
            {selectedCapiLog && (
              <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedCapiLog(null)}>
                <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      {selectedCapiLog.status === 'success' ? <CheckCircle size={20} className="text-green-400" /> : <XCircle size={20} className="text-red-400" />}
                      Detalhes do Evento CAPI
                    </h3>
                    <button onClick={() => setSelectedCapiLog(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
                  </div>
                  <div className="space-y-3">
                    {[
                      { label: 'Status', value: selectedCapiLog.status, color: selectedCapiLog.status === 'success' ? 'text-green-400' : 'text-red-400' },
                      { label: 'Evento', value: selectedCapiLog.event_name || 'Purchase' },
                      { label: 'Cliente', value: selectedCapiLog.customer_name || '-' },
                      { label: 'Telefone', value: selectedCapiLog.customer_phone || '-' },
                      { label: 'Email', value: selectedCapiLog.customer_email || '-' },
                      { label: 'Produto', value: selectedCapiLog.product_name || '-' },
                      { label: 'Valor', value: selectedCapiLog.value ? 'R$ ' + parseFloat(selectedCapiLog.value).toFixed(2) : '-' },
                      { label: 'Plataforma', value: selectedCapiLog.platform || '-' },
                      { label: 'Transaction ID', value: selectedCapiLog.transaction_id || '-' },
                      { label: 'Lead ID', value: selectedCapiLog.lead_id || '-' },
                      { label: 'CTWA CLID', value: selectedCapiLog.ctwa_clid ? selectedCapiLog.ctwa_clid.substring(0, 40) + '...' : '-' },
                      { label: 'WABA ID', value: selectedCapiLog.waba_id || '-' },
                      { label: 'Instância', value: selectedCapiLog.instance_name || '-' },
                      { label: 'Dataset ID', value: selectedCapiLog.dataset_id || '-' },
                      { label: 'Event ID', value: selectedCapiLog.event_id || '-' },
                      { label: 'FB Trace ID', value: selectedCapiLog.fbtrace_id || '-' },
                      { label: 'Data', value: selectedCapiLog.created_at ? new Date(selectedCapiLog.created_at).toLocaleString('pt-BR') : '-' },
                    ].map(item => (
                      <div key={item.label} className="flex justify-between items-start py-2 border-b border-gray-700/50">
                        <span className="text-gray-400 text-sm">{item.label}</span>
                        <span className={"text-sm font-medium text-right max-w-[60%] break-all " + (item.color || 'text-white')}>{item.value}</span>
                      </div>
                    ))}
                    {selectedCapiLog.error_message && (
                      <div className="bg-red-900/20 border border-red-800/30 rounded-lg p-3">
                        <p className="text-red-400 text-xs font-medium mb-1">Erro</p>
                        <p className="text-red-300 text-xs">{selectedCapiLog.error_message}</p>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setSelectedCapiLog(null)} className="w-full mt-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Fechar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Managers */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2"><Building2 className="text-purple-400" />Business Managers</h2>
            <p className="text-gray-500 text-sm">{bms.length} BMs ({bms.filter(b => b.isActive).length} ativas)</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setShowAddBM(true)} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2"><Plus size={18} />Adicionar BM</button>
            {bms.length > 0 && <button type="button" onClick={handleDiscoverAll} disabled={discoveringAll} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">{discoveringAll ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}{discoveringAll ? 'Sincronizando...' : 'Sync Todas'}</button>}
          </div>
        </div>

        {showAddBM && (
          <div className="mb-4 p-4 bg-purple-900/20 border border-purple-700 rounded-xl">
            <h3 className="text-purple-300 font-medium mb-3">Adicionar Business Manager</h3>
            <div className="space-y-3">
              <div><label className="text-gray-400 text-sm block mb-1">Nome (opcional)</label><input type="text" value={newBMName} onChange={(e) => setNewBMName(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" placeholder="BM Principal" /></div>
              <div><label className="text-gray-400 text-sm block mb-1">Access Token *</label>
                <div className="relative"><input type={showTokens.newBM ? 'text' : 'password'} value={newBMToken} onChange={(e) => setNewBMToken(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 pr-12 text-white" placeholder="EAAxxxxxxx..." /><button type="button" onClick={() => setShowTokens({ ...showTokens, newBM: !showTokens.newBM })} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showTokens.newBM ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button type="button" onClick={() => { setShowAddBM(false); setNewBMToken(''); setNewBMName(''); }} className="px-4 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
              <button type="button" onClick={handleAddBM} disabled={addingBM || !newBMToken.trim()} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">{addingBM ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}{addingBM ? 'Validando...' : 'Conectar BM'}</button>
              <span className="text-gray-500 text-sm">ou</span>
              <button type="button" onClick={handleFacebookLogin} disabled={fbConnecting} className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">{fbConnecting ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}{fbConnecting ? 'Conectando...' : 'Login Facebook'}</button>
            </div>
          </div>
        )}

        {bms.length > 0 ? (
          <div className="space-y-2">
            {bms.map(bm => (
              <div key={bm.id} className={`bg-gray-900/50 rounded-lg p-4 border ${bm.isActive ? 'border-purple-500/30' : 'border-gray-700/30 opacity-60'}`}>
                <div className="flex items-center gap-3">
                  <Building2 size={20} className={bm.isActive ? 'text-purple-400' : 'text-gray-500'} />
                  <div className="flex-1">
                    {editingBMName === bm.id ? (
                        <div className="flex items-center gap-2">
                          <input type="text" value={bmNewName} onChange={(e) => setBmNewName(e.target.value)} className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm w-48" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleRenameBM(bm.id); if (e.key === 'Escape') setEditingBMName(null); }} />
                          <button onClick={() => handleRenameBM(bm.id)} className="text-green-400 hover:text-green-300"><Check size={16} /></button>
                          <button onClick={() => setEditingBMName(null)} className="text-gray-400 hover:text-white text-sm">✕</button>
                        </div>
                      ) : (
                        <p className="text-white font-medium cursor-pointer hover:text-blue-400" onClick={() => { setEditingBMName(bm.id); setBmNewName(bm.bmName || ''); }}>{bm.bmName || 'BM ' + bm.bmId}</p>
                      )}
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span>{bm.accountCount} contas</span><span>ID: {bm.bmId}</span>
                      {bm.lastSyncAt && <span>Sync: {new Date(bm.lastSyncAt).toLocaleString('pt-BR')}</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => handleDiscoverBM(bm.id)} disabled={discoveringBM[bm.id]} className="px-3 py-1.5 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg text-sm flex items-center gap-1 disabled:opacity-50">{discoveringBM[bm.id] ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}Sync</button>
                  <button type="button" onClick={() => handleToggleBM(bm.id)} className="p-2 rounded-lg hover:bg-gray-700">{bm.isActive ? <ToggleRight size={20} className="text-green-400" /> : <ToggleLeft size={20} className="text-gray-500" />}</button>
                  <button type="button" onClick={() => handleDeleteBM(bm.id)} className="p-2 text-gray-500 hover:text-red-400"><Trash2 size={18} /></button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500"><Building2 size={40} className="mx-auto mb-2 opacity-50" /><p>Nenhuma BM conectada</p></div>
        )}
      </div>

      {/* Ad Accounts */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          {fbDiscovered && fbDiscovered.businesses && fbDiscovered.businesses.length > 0 && (
            <div className="bg-gray-800 rounded-xl p-6 border border-blue-700/50 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold text-lg">BMs Encontradas ({fbDiscovered.businesses.length})</h3>
                <div className="flex gap-2">
                  <button onClick={handleSetupAll} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium">Configurar Todas</button>
                  <button onClick={() => setFbDiscovered(null)} className="px-3 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm">Fechar</button>
                </div>
              </div>
              <div className="space-y-3">
                {fbDiscovered.businesses.map(bm => (
                  <div key={bm.bmId} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{bm.bmName}</p>
                      <p className="text-gray-500 text-xs">ID: {bm.bmId} - {bm.accountCount} contas</p>
                    </div>
                    {fbSettingUp[bm.bmId] === 'done' ? (
                      <span className="text-green-400 text-sm flex items-center gap-1"><Check size={16} /> Configurada</span>
                    ) : fbSettingUp[bm.bmId] === 'error' ? (
                      <button onClick={() => handleSetupBM(bm)} className="px-3 py-1.5 bg-red-600/50 text-red-300 rounded-lg text-sm">Erro - Tentar novamente</button>
                    ) : (
                      <button onClick={() => handleSetupBM(bm)} disabled={fbSettingUp[bm.bmId] === true} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm disabled:opacity-50">
                        {fbSettingUp[bm.bmId] === true ? 'Configurando...' : 'Configurar'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div><h2 className="text-lg font-bold text-white flex items-center gap-2"><CreditCard className="text-blue-400" />Contas de Anúncio</h2><p className="text-gray-500 text-sm">{adAccounts.length} contas, {selectedCount} selecionadas</p></div>
        </div>
        {adAccounts.length > 0 ? (
          <div className="space-y-2">
            <div className="flex items-center justify-end gap-2 mb-2">
              <button type="button" onClick={() => handleSelectAll(true)} className="text-xs text-blue-400 hover:text-blue-300">Selecionar todas</button>
              <span className="text-gray-600">|</span>
              <button type="button" onClick={() => handleSelectAll(false)} className="text-xs text-gray-400 hover:text-gray-300">Limpar</button>
            </div>
            {groupedAccounts.map(group => (
              <div key={group.businessManagerId || 'no_bm'} className="bg-gray-900/50 rounded-lg overflow-hidden">
                <button type="button" onClick={() => toggleBMExpand(group.businessManagerId || 'no_bm')} className="w-full flex items-center gap-2 p-3 hover:bg-gray-700/30">
                  {expandedBMs[group.businessManagerId || 'no_bm'] ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
                  <Building2 size={18} className="text-purple-400" />
                  <span className="text-white font-medium flex-1 text-left">{group.businessManagerName}</span>
                  <span className="text-gray-500 text-sm">{group.accounts.length} contas</span>
                </button>
                {expandedBMs[group.businessManagerId || 'no_bm'] && (
                  <div className="border-t border-gray-700/50">
                    {group.accounts.map(acc => (
                      <div key={acc.id} className={`flex items-center gap-3 p-3 pl-10 border-b border-gray-700/30 last:border-0 ${acc.isSelected ? 'bg-blue-900/20' : 'hover:bg-gray-700/20'}`}>
                        <button type="button" onClick={() => handleToggleAccount(acc.id)} className={`w-5 h-5 rounded border-2 flex items-center justify-center ${acc.isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-600'}`}>{acc.isSelected && <CheckCircle size={14} className="text-white" />}</button>
                        <CreditCard size={16} className={acc.isSelected ? 'text-blue-400' : 'text-gray-500'} />
                        <div className="flex-1"><p className={acc.isSelected ? 'text-white' : 'text-gray-400'}>{acc.adAccountName}</p><p className="text-gray-500 text-xs">{acc.adAccountId}</p></div>
                        <span className={`px-2 py-0.5 rounded text-xs ${acc.accountStatus === 'ACTIVE' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>{acc.accountStatus === 'ACTIVE' ? 'Ativa' : 'Inativa'}</span>
                        <button type="button" onClick={() => handleDeleteAccount(acc.id)} className="p-1.5 text-gray-500 hover:text-red-400"><Trash2 size={16} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500"><CreditCard size={40} className="mx-auto mb-2 opacity-50" /><p>Nenhuma conta cadastrada</p></div>
        )}
      </div>

      {/* DataCrazy */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">DataCrazy</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">API Token (em branco para manter)</label>
            <div className="relative">
              <input type={showTokens.datacrazy ? 'text' : 'password'} value={config.datacrazyToken} onChange={(e) => setConfig({ ...config, datacrazyToken: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:border-blue-500" placeholder="Cole o token..." />
              <button type="button" onClick={() => setShowTokens({ ...showTokens, datacrazy: !showTokens.datacrazy })} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showTokens.datacrazy ? <EyeOff size={20} /> : <Eye size={20} />}</button>
            </div>
          </div>
          <button type="button" onClick={() => testConnection('datacrazy')} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2">
            {testResults.datacrazy === 'loading' ? <Loader2 className="animate-spin" size={18} /> : testResults.datacrazy === 'success' ? <CheckCircle className="text-green-400" size={18} /> : testResults.datacrazy === 'error' ? <XCircle className="text-red-400" size={18} /> : null}Testar Conexão
          </button>
        </div>
      </div>

      <button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50">
        {saving ? <><Loader2 className="animate-spin" size={20} />Salvando...</> : <><Save size={20} />Salvar Configurações</>}
      </button>
    </form>
  );
};

// ==================== ACCOUNT TAB ====================
const AccountTab = () => {
  const { user } = useAuth();
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [parentUser, setParentUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [profile, setProfile] = useState({ name: '', email: '', currentPassword: '', newPassword: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => { if (user?.id) loadData(); }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [linkedRes, parentRes, devicesRes] = await Promise.all([
        api.get('/users/' + user.id + '/linked').catch(() => ({ data: { linkedUsers: [] } })),
        api.get('/users/' + user.id + '/parent').catch(() => ({ data: { parentUser: null } })),
        api.get('/push/devices').catch(() => ({ data: { devices: [] } }))
      ]);
      setLinkedUsers(linkedRes.data.linkedUsers || []);
      setParentUser(parentRes.data.parentUser || null);
      setDevices(devicesRes.data.devices || []);
      setProfile({ name: user.name || '', email: user.email || '', currentPassword: '', newPassword: '' });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const linkUser = async () => {
    if (!linkEmail.trim()) return;
    setLinkLoading(true);
    try {
      await api.post('/users/' + user.id + '/link', { email: linkEmail.trim() });
      setMessage({ type: 'success', text: 'Usuário vinculado!' });
      setLinkEmail('');
      loadData();
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erro ao vincular' }); }
    setLinkLoading(false);
  };

  const unlinkUser = async (linkedId) => {
    if (!window.confirm('Desvincular este usuário?')) return;
    try { await api.delete('/users/' + user.id + '/link/' + linkedId); loadData(); } catch (e) {}
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/users/' + user.id, { name: profile.name, currentPassword: profile.currentPassword || undefined, newPassword: profile.newPassword || undefined });
      setMessage({ type: 'success', text: 'Perfil atualizado!' });
      setProfile(prev => ({ ...prev, currentPassword: '', newPassword: '' }));
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erro ao salvar' }); }
    setSavingProfile(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{message.text}</span>
        </div>
      )}

      {/* Perfil */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><User size={20} className="text-blue-400" />Meu Perfil</h2>
        <div className="space-y-4">
          <div><label className="block text-gray-400 text-sm mb-2">Nome</label><input type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white" /></div>
          <div><label className="block text-gray-400 text-sm mb-2">Email</label><input type="text" value={profile.email} disabled className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-gray-500" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-400 text-sm mb-2">Senha atual</label><input type="password" value={profile.currentPassword} onChange={(e) => setProfile({ ...profile, currentPassword: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white" placeholder="Preencha para alterar" /></div>
            <div><label className="block text-gray-400 text-sm mb-2">Nova senha</label><input type="password" value={profile.newPassword} onChange={(e) => setProfile({ ...profile, newPassword: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white" placeholder="Nova senha" /></div>
          </div>
          <button onClick={saveProfile} disabled={savingProfile} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">{savingProfile ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Salvar Perfil</button>
        </div>
      </div>

      {/* Usuários Vinculados */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Link2 size={20} className="text-purple-400" />Usuários Vinculados</h2>
        <div className="mb-4 p-3 bg-gray-900/50 border border-gray-700 rounded-lg">
          <p className="text-gray-300 text-sm font-medium mb-1">ℹ️ O que são usuários vinculados?</p>
          <p className="text-gray-400 text-xs leading-relaxed">Usuários vinculados são subcontas que enxergam os <strong className="text-white">seus</strong> dados (vendas, campanhas, webhooks). Diferente da aba <strong className="text-white">Usuários</strong> no menu admin — que gerencia todos os usuários da plataforma e seus planos.</p>
          <p className="text-gray-400 text-xs mt-2">Ao vincular um usuário, você pode definir um <strong className="text-white">% de ajuste</strong> nas comissões exibidas para ele (ex: +10% ou -10% sobre os valores reais).</p>
        </div>
        {parentUser && (
          <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700 rounded-lg">
            <p className="text-blue-300 text-sm">Vinculado a: <strong>{parentUser.name}</strong> ({parentUser.email})</p>
          </div>
        )}
        <div className="space-y-2 mb-4">
          {linkedUsers.map(lu => (
            <div key={lu.id} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
              <div><p className="text-white">{lu.name}</p><p className="text-gray-500 text-xs">{lu.email}</p></div>
              <button onClick={() => unlinkUser(lu.id)} className="p-2 text-gray-500 hover:text-red-400"><Unlink size={18} /></button>
            </div>
          ))}
          {linkedUsers.length === 0 && !parentUser && <p className="text-gray-500 text-sm">Nenhum usuário vinculado</p>}
        </div>
        <div className="flex gap-2">
          <input type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" placeholder="Email do usuário para vincular" />
          <button onClick={linkUser} disabled={linkLoading} className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg disabled:opacity-50">{linkLoading ? <Loader2 size={16} className="animate-spin" /> : 'Vincular'}</button>
        </div>
      </div>

      {/* Dispositivos */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Smartphone size={20} className="text-green-400" />Dispositivos</h2>
        {devices.length > 0 ? devices.map((d, i) => (
          <div key={i} className="flex items-center gap-3 p-3 bg-gray-900/50 rounded-lg mb-2">
            <Smartphone size={18} className="text-green-400" />
            <div className="flex-1"><p className="text-white text-sm">{d.userAgent?.substring(0, 50) || 'Dispositivo ' + (i + 1)}</p><p className="text-gray-500 text-xs">Registrado: {new Date(d.createdAt).toLocaleDateString('pt-BR')}</p></div>
          </div>
        )) : <p className="text-gray-500 text-sm">Nenhum dispositivo registrado</p>}
      </div>

    </div>
  );
};

// ==================== NOTIFICATIONS TAB ====================
const NotificationsTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [testSent, setTestSent] = useState(false);
  const [preferences, setPreferences] = useState({
    newSale: true, saleNoTracking: true, saleRefund: true, chipOffline: true, chipQuality: true, chipReconnected: false,
    dailySummary: true, dailySummaryTime: '21:00', goalReached: false, dailyGoal: 0, webhookError: true, negativeRoi: false, salesDrop: false
  });

  useEffect(() => {
    const init = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      if (supported) { const sub = await isSubscribed(); setPushEnabled(sub); }
      try {
        const r = await api.get('/notifications/preferences');
        if (r.data.preferences) setPreferences(r.data.preferences);
      } catch (e) {}
      setLoading(false);
    };
    init();
  }, []);

  const togglePush = async () => {
    try {
      if (pushEnabled) { await unsubscribeFromPush(); setPushEnabled(false); }
      else { await subscribeToPush(); setPushEnabled(true); }
    } catch (e) { console.error(e); }
  };

  const sendTest = async () => {
    try { await sendTestNotification(); setTestSent(true); setTimeout(() => setTestSent(false), 3000); } catch (e) {}
  };

  const savePreferences = async () => {
    setSaving(true);
    try { await api.post('/notifications/preferences', preferences); } catch (e) {}
    setSaving(false);
  };

  const PreferenceToggle = ({ label, description, field }) => (
    <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
      <div><p className="text-white text-sm">{label}</p>{description && <p className="text-gray-500 text-xs">{description}</p>}</div>
      <button onClick={() => setPreferences(prev => ({ ...prev, [field]: !prev[field] }))} className={`w-12 h-6 rounded-full transition-colors relative ${preferences[field] ? 'bg-blue-600' : 'bg-gray-600'}`}>
        <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${preferences[field] ? 'right-1' : 'left-1'}`} />
      </button>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <div className="space-y-6">
      {/* Push */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Bell size={20} className="text-yellow-400" />Notificações Push</h2>
        {pushSupported ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
              <div><p className="text-white">Notificações Push</p><p className="text-gray-500 text-xs">{pushEnabled ? 'Ativado neste dispositivo' : 'Desativado'}</p></div>
              <button onClick={togglePush} className={`px-4 py-2 rounded-lg ${pushEnabled ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'} text-white`}>{pushEnabled ? 'Desativar' : 'Ativar'}</button>
            </div>
            {pushEnabled && (
              <button onClick={sendTest} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2">
                {testSent ? <><Check size={16} className="text-green-400" />Enviado!</> : <><Send size={16} />Enviar teste</>}
              </button>
            )}
          </div>
        ) : <p className="text-gray-500">Push notifications não suportadas neste navegador</p>}
      </div>

      {/* Preferências */}
      <div className="bg-gray-800 rounded-xl p-6">
        <h2 className="text-lg font-bold text-white mb-4">Preferências</h2>
        <div className="space-y-2">
          <PreferenceToggle label="Nova venda" description="Notificar a cada venda aprovada" field="newSale" />
          <PreferenceToggle label="Venda sem tracking" description="Quando uma venda não tem rastreamento" field="saleNoTracking" />
          <PreferenceToggle label="Reembolso/Chargeback" description="Alertar sobre estornos" field="saleRefund" />
          <PreferenceToggle label="Chip offline" description="Quando um número desconecta" field="chipOffline" />
          <PreferenceToggle label="Qualidade do chip" description="Mudancas na qualidade WhatsApp" field="chipQuality" />
          <PreferenceToggle label="Resumo diario" description="Receber resumo no final do dia" field="dailySummary" />
          {preferences.dailySummary && (
            <div className="pl-4"><label className="text-gray-400 text-xs">Horário do resumo</label><input type="time" value={preferences.dailySummaryTime} onChange={(e) => setPreferences(prev => ({ ...prev, dailySummaryTime: e.target.value }))} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white text-sm ml-2" /></div>
          )}
          <PreferenceToggle label="Erro de webhook" description="Quando um webhook falha" field="webhookError" />
        </div>
        <button onClick={savePreferences} disabled={saving} className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Salvar Preferências
        </button>
      </div>

    </div>
  );
};

// ==================== USERS TAB ====================
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'user', password: '' });
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user', roleId: '' });
  const [rolesList, setRolesList] = useState([]);
  useEffect(() => { api.get('/roles').then(r => setRolesList(r.data.roles || r.data || [])).catch(() => {}); }, []);
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try { const r = await api.get('/auth/users'); setUsers(r.data.users || []); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveEditUser = async () => {
    try {
      await api.put('/auth/users/' + editingUser.id + '/admin-edit', editForm);
      setEditingUser(null);
      loadUsers();
      setMessage({ type: 'success', text: 'Usuário atualizado!' });
    } catch (e) { setMessage({ type: 'error', text: 'Erro ao atualizar' }); }
  };

  const addUser = async () => {
    setAdding(true);
    try {
      await api.post('/auth/users', { ...newUser, roleId: newUser.roleId || null });
      setMessage({ type: 'success', text: 'Usuário criado!' });
      setNewUser({ name: '', email: '', password: '', role: 'user' });
      setShowAdd(false);
      loadUsers();
    } catch (e) { setMessage({ type: 'error', text: e.response?.data?.error || 'Erro' }); }
    setAdding(false);
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Excluir este usuário?')) return;
    try { await api.delete('/auth/users/' + id); loadUsers(); } catch (e) {}
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-500" size={40} /></div>;

  return (
    <div className="space-y-6">
      {message && <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>{message.text}</div>}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2"><Users size={20} className="text-blue-400" />Usuários ({users.length})</h2>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg flex items-center gap-2"><Plus size={18} />Novo Usuário</button>
      </div>

      {showAdd && (
        <div className="bg-gray-800 rounded-xl p-6">
          <h3 className="text-white font-medium mb-4">Novo Usuário</h3>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-gray-400 text-sm mb-1">Nome</label><input type="text" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" /></div>
            <div><label className="block text-gray-400 text-sm mb-1">Email</label><input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" /></div>
            <div><label className="block text-gray-400 text-sm mb-1">Senha</label><input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" /></div>
            <div><label className="block text-gray-400 text-sm mb-1">Cargo</label>
              <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white">
                <option value="user">Usuário</option><option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowAdd(false)} className="px-4 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
            <button onClick={addUser} disabled={adding || !newUser.name || !newUser.email || !newUser.password} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">{adding ? 'Criando...' : 'Criar'}</button>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-900/50 border-b border-gray-700">
            <tr>
              <th className="p-3 text-left text-gray-400 text-sm">Nome</th>
              <th className="p-3 text-left text-gray-400 text-sm">Email</th>
              <th className="p-3 text-center text-gray-400 text-sm">Cargo</th>
              <th className="p-3 text-center text-gray-400 text-sm">Criado em</th>
              <th className="p-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {users.map(u => (
              <tr key={u.id} className="hover:bg-gray-700/30">
                <td className="p-3 text-white">{u.name}</td>
                <td className="p-3 text-gray-400">{u.email}</td>
                <td className="p-3 text-center"><span className={`px-2 py-1 rounded text-xs ${u.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>{u.role}</span>{u.roleId && rolesList.find(r => r.id === u.roleId) && <span className="ml-1 px-2 py-1 rounded text-xs bg-indigo-500/20 text-indigo-300">{rolesList.find(r => r.id === u.roleId).name}</span>}</td>
                <td className="p-3 text-center text-gray-500 text-sm">{new Date(u.createdAt).toLocaleDateString('pt-BR')}</td>
                <td className="p-3 flex gap-1">
                  <button onClick={() => { setEditingUser(u); setEditForm({ name: u.name, email: u.email, role: u.role, roleId: u.roleId || '', password: '' }); }} className="p-2 text-gray-500 hover:text-blue-400"><Pencil size={16} /></button>
                  <button onClick={() => deleteUser(u.id)} className="p-2 text-gray-500 hover:text-red-400"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Editar Usuário */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Editar Usuário</h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-gray-400 text-sm mb-1">Nome</label><input type="text" value={editForm.name} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" /></div>
              <div><label className="block text-gray-400 text-sm mb-1">Email</label><input type="email" value={editForm.email} onChange={e => setEditForm(p => ({...p, email: e.target.value}))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" /></div>
              <div><label className="block text-gray-400 text-sm mb-1">Cargo</label>
                <select value={editForm.role} onChange={e => setEditForm(p => ({...p, role: e.target.value}))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="user">Usuário</option><option value="admin">Admin</option>
                </select>
              </div>
              <div><label className="block text-gray-400 text-sm mb-1">Cargo de permissões</label>
              <select value={editForm.roleId || ''} onChange={e => setEditForm(p => ({...p, roleId: e.target.value}))} className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm">
                <option value="">Sem cargo (acesso total)</option>
                {rolesList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              </div>
              <div><label className="block text-gray-400 text-sm mb-1">Plano</label>
                <select value={editForm.plan || 'free'} onChange={e => setEditForm(p => ({...p, plan: e.target.value}))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="free">Gratuito</option>
                  <option value="pro">Profissional (R$ 97/mês)</option>
                  <option value="enterprise">Enterprise (R$ 297/mês) - Gestão Comercial</option>
                </select>
              </div>
              <div><label className="block text-gray-400 text-sm mb-1">Nova Senha <span className="text-gray-600">(deixe em branco para manter)</span></label><input type="password" value={editForm.password} onChange={e => setEditForm(p => ({...p, password: e.target.value}))} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white" placeholder="Nova senha..." /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEditingUser(null)} className="flex-1 py-2 bg-gray-700 text-white rounded-lg">Cancelar</button>
              <button onClick={saveEditUser} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500">Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
